import { RefObject } from 'react'
import { render, unmountComponentAtNode } from 'react-dom'
import { TextareaSafariProbe } from 'components/features/pfm/TextareaSafariProbe/TextareaSafariProbe'
import { defaultMemoize } from 'reselect'
import { IS_IOS } from 'helpers/userAgentHelpers'

export const rootContainer = document.getElementById('rootContainer')

export const checkTextareaSafari3pxBug = defaultMemoize(() => {
  return new Promise<boolean>((resolve) => {
    if (!IS_IOS) {
      resolve(true)
      return
    }

    const probeNode = document.body.appendChild(document.createElement('div'))

    const handleResult = (isCorrect: boolean) => {
      unmountComponentAtNode(probeNode)
      probeNode.remove()
      resolve(isCorrect)
    }

    render((
      <TextareaSafariProbe
        onResult={ handleResult }
      />
    ), probeNode)
  })
})

export const removeElementById = (elementId: string) => {
  const elem = document.getElementById(elementId)

  if (elem && elem.parentNode) {
    elem.parentNode.removeChild(elem)
  }
}

export const getPageScrollHeight = () =>
  document.documentElement.scrollHeight - document.documentElement.clientHeight

export const loadImage = async (url: string) => {
  const image = new Image()
  image.src = url
  return await new Promise<HTMLImageElement | null>((resolve) => {
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
  })
}

export const loadBlobImage = async (file: Blob) => {
  try {
    const url = URL.createObjectURL(file)
    const result = await loadImage(url)
    URL.revokeObjectURL(url)
    return result
  } catch (e) {
    return null
  }
}

export const pickFirstImage = async (files: File[]) => {
  const images = files.filter((file) => isImageType(file.type))
  const imagesCount = images.length

  let image = images.shift()
  while (image) {
    const result = await loadBlobImage(image)

    if (result && result.naturalWidth >= 200 && result.naturalHeight >= 200) {
      return { image }
    }

    image = images.shift()
  }

  if (imagesCount >= 0) {
    return { isError: true }
  }

  return {}
}

export type ImageSize = {
  width: number
  height: number
}

export const getImageSize = async (url: string): Promise<ImageSize | null> => {
  const image = await loadImage(url)
  if (!image) {
    return null
  }
  return {
    width: image.width,
    height: image.height,
  }
}

export const isImageType = (type: string) => /image\/(jpeg|png|gif)/.test(type)
export const isImageGifType = (type: string) => /image\/(gif)/.test(type)

type GenericStoppableEvent = {
  stopPropagation: () => void
}
export const stopPropagation = <Event extends GenericStoppableEvent>(handler?: (event: Event) => any) => (event: Event) => {
  event.stopPropagation()
  handler && handler(event)
}

const isRefContainsTarget = (ref: RefObject<HTMLDivElement>, target: HTMLElement | null) => {
  return ref && target && ref.current && (ref.current.contains(target) || ref.current === target)
}

export const filterEventsFromNode = (ref: RefObject<HTMLDivElement>, callback: () => void) => (event?: KeyboardEvent) => {
  if (event && event.target && isRefContainsTarget(ref, event.target as HTMLElement)) {
    return
  }
  callback()
}

export type RectOffset = {
  left: number
  top: number
} | null
export function getOffset (containerNode: HTMLElement | null, targetNode: HTMLElement | null): RectOffset {
  if (!containerNode || !targetNode) {
    return null
  }

  const containerRect = containerNode.getBoundingClientRect()
  const targetRect = targetNode.getBoundingClientRect()

  return {
    left: targetRect.left - containerRect.left + targetRect.width * 0.5,
    top: targetRect.top - containerRect.top + targetRect.height * 0.5,
  }
}

export function getTitle () {
  return document.title
}

export function setTitle (title: string) {
  document.title = title
}

export function getDescription () {
  const descriptionNode = document.querySelector('meta[name="description"]')
  return descriptionNode ? descriptionNode.getAttribute('content') || '' : ''
}

export function setDescription (content: string) {
  let descriptionNode = document.querySelector('meta[name="description"]')
  if (!descriptionNode) {
    descriptionNode = document.createElement('meta')
    descriptionNode.setAttribute('name', 'description')
  }
  descriptionNode.setAttribute('content', content)
}

export function scheduleCallOnTransitionEnd (node: HTMLElement, maxTime: number) {
  let timeoutHandle: number | null
  let transitionEndHandler: EventListener | null
  const timeoutPromise: Promise<void> = new Promise((resolve) => {
    timeoutHandle = window.setTimeout(() => {
      resolve()
    }, maxTime)
  })

  const transitionEndPromise = new Promise((resolve) => {
    transitionEndHandler = resolve
    node.addEventListener('transitionend', transitionEndHandler)
  })

  return Promise.race([timeoutPromise, transitionEndPromise]).then((result) => {
    if (node && transitionEndHandler) {
      node.removeEventListener('transitionend', transitionEndHandler)
    }
    timeoutHandle && clearTimeout(timeoutHandle)
    timeoutHandle = null

    return result
  })
}

export function insertTextHistorySafe (inputNode: HTMLTextAreaElement, text: string) {
  const { selectionStart, selectionEnd } = inputNode
  const cursorOffset = selectionStart + text.length

  if (document.queryCommandSupported('insertText')) {
    const isSuccess = document.execCommand('insertText', false, text)
    if (isSuccess) {
      return true
    }
  }

  // workaround due https://bugzilla.mozilla.org/show_bug.cgi?id=1220696
  // history will be truncated
  if (inputNode.setRangeText) {
    inputNode.setRangeText(text, selectionStart, selectionEnd)
  } else {
    const value = inputNode.value
    inputNode.value = `${ value.substring(0, selectionStart) }${ text }${ value.substr(selectionEnd) }`
  }

  inputNode.setSelectionRange(cursorOffset, cursorOffset)
  return false
}
